import React, { useState } from "react";
import moment from "moment";

import ModalConfirmStart from "../ModalConfirmStart";
import * as S from "./styles";

const WEEKDAYS = [
  "domingo",
  "segunda",
  "terça",
  "quarta",
  "quinta",
  "sexta",
  "sábado",
];

const formatWeekdayTime = (date) => {
  if (!date) return null;
  const m = moment(date);
  if (!m.isValid()) return null;
  const weekday = WEEKDAYS[m.day()];
  const hour = m.hour();
  const minute = m.minute();
  const time =
    minute === 0 ? `${hour}h` : `${hour}h${String(minute).padStart(2, "0")}`;
  return `${weekday} às ${time}`;
};

const formatCloseWindow = (endDate) => {
  const moment_ = formatWeekdayTime(endDate);
  return moment_ ? `Janela de início fecha ${moment_}` : null;
};

const formatDurationFromMinutes = (minutes) => {
  const total = Number(minutes);
  if (!Number.isFinite(total) || total <= 0) return null;
  const hours = Math.floor(total / 60);
  const mins = total % 60;
  if (hours > 0 && mins === 0) return `${hours}h`;
  if (hours > 0) return `${hours}h${String(mins).padStart(2, "0")}`;
  return `${mins}min`;
};

const buildRules = (gabaritoLabel) => [
  <>
    Você poderá realizar o simulado apenas <strong>uma única vez</strong>.
  </>,
  <>
    As respostas são <strong>objetivas</strong> e existe apenas{" "}
    <strong>uma alternativa certa por questão.</strong> Você pontuará apenas
    se marcar a resposta correta.
  </>,
  <>
    <strong>Não é possível parar o simulado</strong>. O tempo continuará
    correndo até que você encerre o simulado ou o tempo se esgote. Caso o
    tempo se encerre, questões em branco (não respondidas) serão consideradas
    erradas.
  </>,
  <>
    <strong>O gabarito</strong> e a <strong>resolução</strong> das questões
    estarão disponíveis {gabaritoLabel}. Você pode acessar o gabarito realizando
    o mesmo login da inscrição do Craques do Enem.
  </>,
  <>
    As <strong>pontuações</strong> serão calculadas de acordo com o{" "}
    <strong>número de acertos</strong>, por exemplo, se você acertar 8
    questões sua pontuação será igual a 8,0.
  </>,
];

export default function Instructions({
  loading,
  doStartSimulation,
  simulated,
  badgeLabel = "SIMULADO LIBERADO · VOCÊ FOI CONVOCADO",
  title = "Pronto pra entrar em campo?",
  subtitle,
  questionsCount,
  duration,
  startLabel,
  footerNote,
  isResuming,
}) {
  const [isConfirmOpen, setConfirmOpen] = useState(false);
  // Guarda o estado "resuming" no instante da confirmação. Sem isso, ao iniciar
  // a prova o histórico volta com startDate e os textos trocam para a variante
  // "CONTINUAR/RETOMAR" num render intermediário, antes das questões aparecerem.
  const [frozenResuming, setFrozenResuming] = useState(null);

  const resolvedQuestionsCount =
    questionsCount ||
    (simulated && simulated.questions && simulated.questions.length) ||
    "60";

  const resolvedDuration =
    duration ||
    (simulated && formatDurationFromMinutes(simulated.duration)) ||
    "4h";

  // Tentativa em andamento (já existe registro no SimulationLandingEntries)
  // => textos de "retomar" (o cronômetro já está correndo). Usa o flag vindo
  // do verify; se não for informado, cai no isStarted do simulado.
  const liveResuming =
    typeof isResuming === "boolean"
      ? isResuming
      : !!(simulated && simulated.isStarted);

  // Após confirmar o início, usa o valor congelado para não trocar os textos.
  const resuming = frozenResuming !== null ? frozenResuming : liveResuming;

  const resolvedStartLabel =
    startLabel || (resuming ? "CONTINUAR SIMULADO" : "COMEÇAR SIMULADO");

  const resolvedBadgeLabel = resuming
    ? "VOCÊ JÁ ESTÁ EM CAMPO · RETOMAR"
    : badgeLabel;

  const resolvedTitle = resuming ? "Bora voltar pra prova?" : title;

  const resolvedSubtitle =
    subtitle ||
    (resuming
      ? "Você já começou este simulado e o cronômetro não parou — o tempo continua correndo. Clique em continuar pra voltar de onde parou."
      : `${resolvedQuestionsCount} questões no novo formato do ENEM. Quando você começar, o cronômetro de ${resolvedDuration} dispara e não para. Garante que tá num momento tranquilo.`);

  const resolvedFooterNote =
    footerNote ||
    (simulated && formatCloseWindow(simulated.endDate)) ||
    "Janela de início fecha sexta às 12h";

  // Horário de liberação do gabarito vem do backend (rankingStartDate)
  const gabaritoLabel =
    (simulated && formatWeekdayTime(simulated.rankingStartDate)) ||
    "sexta às 18h";
  const rules = buildRules(gabaritoLabel);

  const handleStartClick = () => setConfirmOpen(true);
  const handleCancel = () => {
    if (!loading) setConfirmOpen(false);
  };
  const handleConfirm = () => {
    setFrozenResuming(resuming);
    setConfirmOpen(false);
    if (typeof doStartSimulation === "function") doStartSimulation();
  };

  return (
    <S.Container>
      <S.Card>
        <S.Badge>
          <S.BadgeDot />
          <S.BadgeText>{resolvedBadgeLabel}</S.BadgeText>
        </S.Badge>

        <S.Title>{resolvedTitle}</S.Title>

        {resolvedSubtitle && <S.Subtitle>{resolvedSubtitle}</S.Subtitle>}

        <S.StatsRow>
          <S.StatCard>
            <S.StatValue>{resolvedQuestionsCount}</S.StatValue>
            <S.StatLabel>QUESTÕES</S.StatLabel>
          </S.StatCard>
          <S.StatCard>
            <S.StatValue>{resolvedDuration}</S.StatValue>
            <S.StatLabel>PRA CONCLUIR</S.StatLabel>
          </S.StatCard>
        </S.StatsRow>

        <S.RulesBox>
          <S.RulesTitle>COMO FUNCIONA</S.RulesTitle>
          {rules.map((rule, index) => (
            <S.RuleItem key={index}>
              <S.RuleNumber>{index + 1}</S.RuleNumber>
              <S.RuleText>{rule}</S.RuleText>
            </S.RuleItem>
          ))}
        </S.RulesBox>

        {doStartSimulation && (
          <S.StartButton
            type="button"
            loading={loading}
            onClick={handleStartClick}
          >
            {resolvedStartLabel}
            <S.StartArrow>→</S.StartArrow>
          </S.StartButton>
        )}

        {resolvedFooterNote && <S.FooterNote>{resolvedFooterNote}</S.FooterNote>}
      </S.Card>

      <ModalConfirmStart
        isOpen={isConfirmOpen}
        loading={loading}
        title={resuming ? "Voltar pra prova?" : undefined}
        description={
          resuming
            ? "Seu cronômetro não parou — o tempo continua correndo. Clique pra retomar de onde você parou."
            : `O cronômetro de ${resolvedDuration} dispara agora e não para — nem se você fechar a aba. Comece só quando estiver num momento tranquilo.`
        }
        confirmLabel={resuming ? "SIM, CONTINUAR" : undefined}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </S.Container>
  );
}
